import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCard, ShieldCheck, Lock, Clock, CheckCircle, ChevronLeft } from 'lucide-react';

const PLAN_DETAILS = {
  free: {
    id: 'free',
    name: 'Free',
    description: 'Light exploration with essential access.',
    monthly: { usd: 0, note: 'Includes core features' },
    annual: { usd: 0, note: 'No payment required' },
    features: [
      '50 chat prompts per month',
      '2 AI image generations',
      '1 GB secure storage',
      'Access to free courses',
      'Community view-only mode'
    ]
  },
  'learner-plus': {
    id: 'learner-plus',
    name: 'Learner Plus',
    description: 'Ideal for students and hobbyists expanding their toolkit.',
    monthly: { usd: 12, note: 'Cancel anytime' },
    annual: { usd: 120, note: 'Save 17% with annual billing' },
    features: [
      'Up to 2M chat tokens monthly',
      '30 AI image generations',
      '10 GB cloud storage',
      'Exclusive course bundles & discounts',
      'Basic CPD tracking dashboards'
    ]
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    description: 'For practicing professionals managing full client pipelines.',
    monthly: { usd: 29, note: 'Enterprise invoices available' },
    annual: { usd: 290, note: '2 months free with annual billing' },
    features: [
      'Up to 6M chat tokens monthly',
      '100 AI image generations',
      '50 GB storage with version history',
      'Advanced CPD ledger & exports',
      'Studio, Pin-Up, and priority support'
    ]
  }
};

const currencyOptions = [
  { code: 'USD', label: 'USD', rate: 1 },
  { code: 'AED', label: 'AED', rate: 3.67 },
  { code: 'SAR', label: 'SAR', rate: 3.75 },
  { code: 'BHD', label: 'BHD', rate: 0.38 }
];

const Payment = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialPlan = searchParams.get('plan') || localStorage.getItem('selectedPlan') || 'free';
  const [selectedPlan, setSelectedPlan] = useState(initialPlan);
  const [billingCycle, setBillingCycle] = useState(searchParams.get('billing') === 'annual' ? 'annual' : 'monthly');
  const [currency, setCurrency] = useState('USD');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    company: '',
    cardNumber: '',
    expiry: '',
    cvv: '',
    rememberCard: false,
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateRegion: '',
    country: '',
    postalCode: '',
    agreeTerms: false,
  });

  const plan = PLAN_DETAILS[selectedPlan] || PLAN_DETAILS.free;
  const isFreePlan = selectedPlan === 'free';

  const priceSummary = useMemo(() => {
    const pricing = plan[billingCycle];
    const baseUsd = pricing.usd;
    const currencyInfo = currencyOptions.find(opt => opt.code === currency) || currencyOptions[0];
    const converted = baseUsd * currencyInfo.rate;
    return {
      baseUsd,
      converted: converted.toFixed(2),
      note: pricing.note,
      currency: currencyInfo.code
    };
  }, [plan, billingCycle, currency]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!form.fullName.trim() || !form.email.trim()) {
      setError('Please provide your full name and email before continuing.');
      return;
    }

    if (!form.agreeTerms) {
      setError('You must agree to the billing terms to continue.');
      return;
    }

    if (!isFreePlan) {
      const requiredCardFields = [
        { value: form.cardNumber.trim(), label: 'card number' },
        { value: form.expiry.trim(), label: 'card expiry' },
        { value: form.cvv.trim(), label: 'card security code' },
        { value: form.addressLine1.trim(), label: 'address line 1' },
        { value: form.addressLine2.trim(), label: 'address line 2' },
        { value: form.city.trim(), label: 'city' },
        { value: form.stateRegion.trim(), label: 'state / region' },
        { value: form.country.trim(), label: 'country' },
        { value: form.postalCode.trim(), label: 'postal code' },
      ];

      const missingField = requiredCardFields.find(field => !field.value);
      if (missingField) {
        setError(`Please fill in your ${missingField.label} to continue.`);
        return;
      }
    }

    setProcessing(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1500)); // Mock network latency

      localStorage.setItem('activePlan', selectedPlan);
      localStorage.setItem('billingCycle', billingCycle);
      localStorage.setItem('subscriptionStatus', 'active');

      navigate('/app', { replace: true, state: { paymentSuccess: true, plan: selectedPlan } });
    } catch (err) {
      setError('There was an issue processing your payment. Please try again or use a different card.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF6F6] via-white to-[#F9EDED] text-gray-900">
      <header className="border-b border-[#E6B6B6] bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <Link to="/" className="text-2xl font-semibold tracking-wide text-[#AC5757]">
            AXORA
          </Link>
          <div className="flex items-center gap-4 text-sm font-medium text-gray-600">
            <ShieldCheck size={16} className="text-[#AC5757]" />
            Secure SSL 256-bit encryption
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12 grid gap-8 lg:grid-cols-[2fr_1fr]">
        <section className="bg-white rounded-3xl shadow-xl border border-[#F2C8C8] p-8 space-y-8">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#AC5757] hover:text-[#8A4A4A]"
          >
            <ChevronLeft size={18} />
            Back
          </button>

          <div className="flex flex-col gap-6 border border-[#F2C8C8] rounded-2xl p-6 bg-[#FFF8F8]">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#AC5757]">Select Plan</p>
              <h1 className="text-3xl font-judson font-bold mt-2 text-gray-900">Complete your {plan.name} access</h1>
            </div>

            <div className="flex flex-wrap gap-3">
              {Object.values(PLAN_DETAILS).map(option => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setSelectedPlan(option.id)}
                  className={`flex-1 min-w-[150px] rounded-2xl border px-4 py-3 text-left transition ${
                    selectedPlan === option.id
                      ? 'border-[#AC5757] bg-[#AC5757]/10 text-[#AC5757]'
                      : 'border-[#F2C8C8] hover:border-[#AC5757]/60 text-gray-600'
                  }`}
                >
                  <p className="text-sm font-semibold">{option.name}</p>
                  <p className="text-xs opacity-80 mt-1">{option.description}</p>
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex gap-2 bg-white rounded-full border border-[#F2C8C8] p-1">
                <button
                  type="button"
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-4 py-2 text-sm font-semibold rounded-full ${
                    billingCycle === 'monthly' ? 'bg-[#AC5757] text-white' : 'text-[#AC5757]'
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBillingCycle('annual')}
                  className={`px-4 py-2 text-sm font-semibold rounded-full ${
                    billingCycle === 'annual' ? 'bg-[#AC5757] text-white' : 'text-[#AC5757]'
                  }`}
                >
                  Annual
                </button>
              </div>

              <div className="flex items-center gap-2">
                <Clock size={16} className="text-[#AC5757]" />
                <p className="text-sm text-[#8A4A4A]">{plan[billingCycle].note}</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full name</label>
                <input
                  type="text"
                  name="fullName"
                  value={form.fullName}
                  onChange={handleInputChange}
                  placeholder="Riya Kapoor"
                  required
                  className="w-full rounded-xl border border-[#F2C8C8] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#AC5757]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email address</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleInputChange}
                  placeholder="riya.kapoor@email.com"
                  required
                  className="w-full rounded-xl border border-[#F2C8C8] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#AC5757]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company (optional)</label>
              <input
                type="text"
                name="company"
                value={form.company}
                onChange={handleInputChange}
                placeholder="Studio / University / Firm"
                className="w-full rounded-xl border border-[#F2C8C8] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#AC5757]"
              />
            </div>

            {!isFreePlan && (
              <>
                <div className="space-y-4">
                  <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <CreditCard size={18} className="text-[#AC5757]" />
                    Card details
                  </p>
                  <input
                    type="text"
                    name="cardNumber"
                    value={form.cardNumber}
                    onChange={handleInputChange}
                    placeholder="Card number"
                    inputMode="numeric"
                    maxLength={19}
                    className="w-full rounded-xl border border-[#F2C8C8] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#AC5757]"
                  />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input
                      type="text"
                      name="expiry"
                      value={form.expiry}
                      onChange={handleInputChange}
                      placeholder="MM / YY"
                      className="w-full rounded-xl border border-[#F2C8C8] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#AC5757]"
                    />
                    <input
                      type="password"
                      name="cvv"
                      value={form.cvv}
                      onChange={handleInputChange}
                      placeholder="CVV"
                      maxLength={4}
                      className="w-full rounded-xl border border-[#F2C8C8] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#AC5757]"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      name="rememberCard"
                      checked={form.rememberCard}
                      onChange={handleInputChange}
                      className="rounded border-[#F2C8C8] text-[#AC5757] focus:ring-[#AC5757]"
                    />
                    Save this card for future purchases
                  </label>
                </div>

                <div className="space-y-4">
                  <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    Billing address
                  </p>
                  <div className="grid gap-4">
                    <input
                      type="text"
                      name="addressLine1"
                      value={form.addressLine1}
                      onChange={handleInputChange}
                      placeholder="Address line 1"
                      className="w-full rounded-xl border border-[#F2C8C8] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#AC5757]"
                    />
                    <input
                      type="text"
                      name="addressLine2"
                      value={form.addressLine2}
                      onChange={handleInputChange}
                      placeholder="Address line 2"
                      className="w-full rounded-xl border border-[#F2C8C8] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#AC5757]"
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input
                      type="text"
                      name="city"
                      value={form.city}
                      onChange={handleInputChange}
                      placeholder="City"
                      className="w-full rounded-xl border border-[#F2C8C8] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#AC5757]"
                    />
                    <input
                      type="text"
                      name="stateRegion"
                      value={form.stateRegion}
                      onChange={handleInputChange}
                      placeholder="State / Province / Region"
                      className="w-full rounded-xl border border-[#F2C8C8] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#AC5757]"
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input
                      type="text"
                      name="country"
                      value={form.country}
                      onChange={handleInputChange}
                      placeholder="Country"
                      className="w-full rounded-xl border border-[#F2C8C8] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#AC5757]"
                    />
                    <input
                      type="text"
                      name="postalCode"
                      value={form.postalCode}
                      onChange={handleInputChange}
                      placeholder="Postal code / ZIP"
                      className="w-full rounded-xl border border-[#F2C8C8] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#AC5757]"
                    />
                  </div>
                </div>
              </>
            )}

            <label className="flex items-start gap-3 text-sm text-gray-600">
              <input
                type="checkbox"
                name="agreeTerms"
                checked={form.agreeTerms}
                onChange={handleInputChange}
                className="mt-1 rounded border-[#F2C8C8] text-[#AC5757] focus:ring-[#AC5757]"
              />
              <span>
                I authorize Axora to securely store my payment method and bill according to the selected plan.
                I agree to the{' '}
                <Link to="/terms" className="text-[#AC5757] font-semibold hover:text-[#8A4A4A]">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-[#AC5757] font-semibold hover:text-[#8A4A4A]">
                  Privacy Policy
                </Link>
                .
              </span>
            </label>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={processing}
              className="w-full rounded-2xl bg-[#AC5757] py-4 text-lg font-semibold text-white shadow-lg hover:bg-[#8A4A4A] transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {processing ? (
                <>
                  Processing…
                </>
              ) : isFreePlan ? (
                <>
                  <CheckCircle size={18} className="text-white/90" />
                  Activate free plan
                </>
              ) : (
                <>
                  <Lock size={18} className="text-white/90" />
                  Complete secure payment
                </>
              )}
            </button>

            <p className="text-xs text-gray-500 text-center">
              This is a staged checkout experience for the Axora prototype. In production, this would connect to a PCI-compliant provider (Stripe, Paddle, Adyen, etc.).
            </p>
          </form>
        </section>

        <aside className="space-y-6">
          <div className="bg-white rounded-3xl shadow-xl border border-[#F2C8C8] p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#AC5757]">Order Summary</p>
                <h2 className="text-2xl font-judson font-bold text-gray-900 mt-1">{plan.name} Plan</h2>
              </div>
              <select
                value={currency}
                onChange={(event) => setCurrency(event.target.value)}
                className="border border-[#F2C8C8] text-sm rounded-full px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#AC5757]"
              >
                {currencyOptions.map(option => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="rounded-2xl bg-[#FFF6F6] border border-[#F2C8C8] p-4 space-y-3">
              <div className="flex items-baseline justify-between">
                <p className="text-sm text-gray-500">Plan price ({billingCycle})</p>
                <p className="text-lg font-semibold text-[#AC5757]">
                  {priceSummary.currency} {priceSummary.converted}
                </p>
              </div>
              <div className="flex items-baseline justify-between text-sm">
                <p className="text-gray-500">Equivalent in USD</p>
                <p className="font-medium">${priceSummary.baseUsd}</p>
              </div>
              <p className="text-xs text-[#8A4A4A]">{priceSummary.note}</p>
            </div>

            <ul className="space-y-2 text-sm text-gray-700">
              {plan.features.map(feature => (
                <li key={feature} className="flex items-start gap-2">
                  <CheckCircle size={16} className="mt-[2px] text-[#AC5757]" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            {!isFreePlan ? (
              <div className="rounded-2xl border border-[#F2C8C8] bg-white p-4 space-y-2">
                <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <ShieldCheck size={16} className="text-[#AC5757]" />
                  Secure payment
                </p>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Payments are encrypted end-to-end. Sensitive card data never touches Axora servers; instead, it is tokenized by a PCI Level 1 provider.
                </p>
              </div>
            ) : (
              <div className="rounded-2xl border border-[#F2C8C8] bg-white p-4 space-y-2">
                <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <CheckCircle size={16} className="text-[#AC5757]" />
                  No billing details required
                </p>
                <p className="text-xs text-gray-600 leading-relaxed">
                  The Free plan activates instantly with your Axora account—no payment method needed. Upgrade anytime for full CPD tracking, studio tools, and premium credits.
                </p>
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-[#F2C8C8] bg-white p-6 space-y-3 text-sm text-gray-600">
            <p className="font-semibold text-gray-800">Need help?</p>
            <p>Email <a href="mailto:billing@axora.com" className="text-[#AC5757] font-semibold">billing@axora.com</a> for invoicing, procurement approvals, or regional pricing questions.</p>
            <p className="text-xs text-gray-500">VAT/GST will be calculated where applicable after you enter your billing address.</p>
          </div>
        </aside>
      </main>
    </div>
  );
};

export default Payment;

